import { useEffect, useState } from 'preact/hooks';
import type { QueueBody } from '../../shared/pr/api';
import type { AppRole } from '../../shared/roles';
import { useI18n, type MessageKey } from '../i18n';
import { apiGet } from '../lib/api';

// "Your work" on Home for Procurement, the CFO and Finance (self-review C4): counts from the
// lists they work from, each linking there. A list that fails to load simply shows no tile.

interface Tile {
  key: MessageKey;
  value: number;
  href: string;
  warn?: boolean;
}

async function tilesFor(roles: readonly AppRole[]): Promise<Tile[]> {
  const jobs: Promise<Tile[]>[] = [];
  if (roles.includes('Procurement')) {
    jobs.push(
      apiGet<QueueBody>('/api/queue').then((q) => [
        { key: 'home.work.toReview', value: q.counts.toReview, href: '#/review' },
        { key: 'home.work.sourcing', value: q.counts.sourcing, href: '#/queue' },
        {
          key: 'home.work.pastTarget',
          value: q.counts.pastTarget,
          href: '#/queue',
          warn: q.counts.pastTarget > 0,
        },
      ]),
    );
  }
  if (roles.includes('CFO')) {
    jobs.push(
      apiGet<{ items: unknown[] }>('/api/po-approvals').then((b) => [
        {
          key: 'home.work.cfo',
          value: b.items.length,
          href: '#/po-approvals',
          warn: b.items.length > 0,
        },
      ]),
    );
  }
  if (roles.includes('Finance')) {
    jobs.push(
      apiGet<{ toReceive: unknown[]; toPay: unknown[] }>('/api/finance/inbox').then((b) => [
        { key: 'home.work.toReceive', value: b.toReceive.length, href: '#/payments' },
        { key: 'home.work.toPay', value: b.toPay.length, href: '#/payments' },
      ]),
    );
  }
  const settled = await Promise.allSettled(jobs);
  return settled.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
}

export function WorkTiles({ roles }: { roles: readonly AppRole[] }) {
  const { t } = useI18n();
  const [tiles, setTiles] = useState<Tile[]>([]);
  const key = roles.join(',');
  useEffect(() => {
    let live = true;
    void tilesFor(roles).then((x) => live && setTiles(x));
    return () => {
      live = false;
    };
  }, [key]);
  if (!tiles.length) return null;
  return (
    <section aria-labelledby="home-work" class="stack">
      <h2 id="home-work" class="card__title">
        {t('home.work.title')}
      </h2>
      <ul class="tiles">
        {tiles.map((tile) => (
          <li
            key={tile.key}
            class={`tile tile--tone tile--tone-${tile.key.split('.').pop() ?? ''}${tile.warn ? ' tile--warning' : ''}`}
          >
            <a class="tile__link" href={tile.href}>
              <span class="caption">{t(tile.key)}</span>
              <span class="tile__value">{tile.value}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
